import React, { useState, useEffect } from 'react';
import { HOUSE_TYPES, FLOOR_OPTIONS, BIYAANO_FEATURES, DESTINATION_TYPES } from '../utils/constants';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getInvoice, updateInvoice, deleteInvoice } from '../services/firestoreService';
import { generateInvoicePDF } from '../services/pdfService';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import Loading from '../components/common/Loading';
import { formatDate, getServiceTypeLabel, formatCurrency } from '../utils/helpers';
import {
  formatItemSelectionLabel,
  isLegacyMukayfRow,
  normalizeGuryahaItemName
} from '../utils/helpers';
import toast from 'react-hot-toast';
import WorkShiftsEditor from '../components/quotation/WorkShiftsEditor';
import { useApp } from '../context/AppContext';

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { employees } = useApp();
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  
  const [editData, setEditData] = useState({
    projectAmount: 0,
    xamaaliAmount: 0,
    editReason: ''
  });

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    setLoading(true);
    const result = await getInvoice(id);
    if (result.success) {
      setInvoice(result.data);
      setEditData({
        projectAmount: result.data.projectAmount || 0,
        xamaaliAmount: result.data.xamaaliAmount || 0,
        editReason: ''
      });
    } else {
      toast.error('Invoice lama helin');
      navigate('/admin/invoices');
    }
    setLoading(false);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: name === 'editReason' ? value : parseFloat(value) || 0
    }));
  };

  const handleUpdate = async () => {
    if (!editData.editReason.trim()) {
      toast.error('Fadlan qor sababta');
      return;
    }
    setSubmitting(true);
    const result = await updateInvoice(id, {
      projectAmount: editData.projectAmount,
      xamaaliAmount: editData.xamaaliAmount
    }, user?.uid, user?.email || 'Admin', editData.editReason);

    if (result.success) {
      toast.success('Waa la cusbooneysiiyay');
      setShowEditModal(false);
      fetchInvoice();
    } else {
      toast.error('Khalad: ' + result.error);
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    setSubmitting(true);
    const result = await deleteInvoice(id);
    if (result.success) {
      toast.success('Waa la tirtiray');
      navigate('/admin/invoices');
    } else {
      toast.error('Khalad: ' + result.error);
    }
    setSubmitting(false);
  };

  const handlePrint = () => window.print();

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      await generateInvoicePDF({ ...invoice, pdfEmployees: employees });
      toast.success('PDF waa la soo dejiyay!');
    } catch (error) {
      console.error('PDF Error:', error);
      toast.error('Khalad PDF: ' + error.message);
    }
    setDownloadingPDF(false);
  };

  const renderItems = () => {
    const items = invoice?.items;
    if (!items || !Array.isArray(items) || items.length === 0) {
      return <p className="text-gray-500 text-center py-4">Alaab lama dooran</p>;
    }

    if (invoice.serviceType === 'gurista-guryaha') {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-2 text-left">Alaabta</th>
                <th className="px-2 py-2 text-left">Nooca</th>
                <th className="px-2 py-2 text-center">Dhaawac</th>
                <th className="px-2 py-2 text-center">Qty</th>
                <th className="px-2 py-2 text-left">Faallo</th>
              </tr>
            </thead>
            <tbody>
              {items.filter((item) => !isLegacyMukayfRow(item)).map((item, idx) => (
                <tr key={idx} className="border-b">
                  <td className="px-2 py-2 font-medium">{normalizeGuryahaItemName(item.itemName) || '-'}</td>
                  <td className="px-2 py-2">{formatItemSelectionLabel(item.selection)}</td>
                  <td className="px-2 py-2 text-center">{item.damaged ? '⚠️' : ''}</td>
                  <td className="px-2 py-2 text-center">{item.quantity || 0}</td>
                  <td className="px-2 py-2">{item.notes || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (invoice.serviceType === 'gurista-xafiisyada') {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left">Alaabta</th>
                <th className="px-3 py-2 text-center">Dhaawac</th>
                <th className="px-3 py-2 text-center">Qty</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className="border-b">
                  <td className="px-3 py-2 font-medium">{item.itemName || '-'}</td>
                  <td className="px-3 py-2 text-center">{item.damaged ? '⚠️' : ''}</td>
                  <td className="px-3 py-2 text-center">{item.quantity || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (invoice.serviceType === 'gurista-ganacsiyada') {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left">Sharaxaad</th>
                <th className="px-3 py-2 text-center">Qty</th>
                <th className="px-3 py-2 text-left">Notes</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className="border-b">
                  <td className="px-3 py-2 font-medium">{item.description || '-'}</td>
                  <td className="px-3 py-2 text-center">{item.quantity || 0}</td>
                  <td className="px-3 py-2">{item.notes || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  if (loading) return <Loading fullScreen />;

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Invoices lama helin</p>
        <Button onClick={() => navigate('/admin/invoices')} className="mt-4">Dib u noqo</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header - no-print */}
      <div className="no-print">
        <button
          onClick={() => navigate('/admin/invoices')}
          className="text-gray-500 hover:text-gray-700 mb-2 flex items-center text-sm"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Dib u noqo
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-lg md:text-xl font-bold text-gray-900">#{invoice.invoiceNumber}</h1>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="secondary" 
              size="sm"
              onClick={handleDownloadPDF} 
              loading={downloadingPDF}
              icon={<span>📥</span>}
            >
              PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} icon={<span>🖨️</span>}>
              Print
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowEditModal(true)} icon={<span>✏️</span>}>
              Edit
            </Button>
            <Button variant="danger" size="sm" onClick={() => setShowDeleteModal(true)} icon={<span>🗑️</span>}>
              Del
            </Button>
          </div>
        </div>
      </div>

      {/* Invoice - Print Area */}
      <div id="invoice-print-area" className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-700 to-red-800 text-white p-4 md:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center">
              <img src="/logo.png" alt="Logo" className="h-10 md:h-14 w-auto bg-white rounded-lg p-1 md:p-2" />
              <div className="ml-3">
                <h2 className="text-lg md:text-xl font-bold">SAHALGUUR</h2>
                <p className="opacity-90 text-xs md:text-sm">Adeegga Guuritaanka</p>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-lg md:text-xl font-bold">{invoice.invoiceNumber}</p>
              <p className="opacity-90 text-xs md:text-sm">{formatDate(invoice.createdAt)}</p>
            </div>
          </div>
        </div>

        {/* Customer Info */}
        <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 border-b">
          <div>
            <h3 className="text-xs font-medium text-gray-500 mb-1">MACMIILKA</h3>
            <p className="font-bold">{invoice.customerName}</p>
            <p className="text-gray-600 text-sm">{invoice.phone}</p>
          </div>
          <div>
            <h3 className="text-xs font-medium text-gray-500 mb-1">GUURITAANKA</h3>
            <p className="text-sm"><span className="text-gray-500">Ka:</span> {invoice.fromLocation}</p>
            <p className="text-sm"><span className="text-gray-500">Ilaa:</span> {invoice.toLocation}</p>
            <p className="text-sm"><span className="text-gray-500">Taariikh:</span> {formatDate(invoice.movingDate)}</p>
          </div>
          
        </div>

        {/* Service Type */}
        <div className="px-4 md:px-6 py-2 bg-gray-50 border-b">
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
            {getServiceTypeLabel(invoice.serviceType)}
          </span>
        </div>

        {(invoice.houseType || invoice.destinationType) && (
          <div className="px-4 md:px-6 py-3 border-b bg-orange-50/40">
            <h3 className="text-xs font-medium text-gray-500 mb-2">NOOCA GURIGA</h3>
            <div className="flex flex-wrap gap-2 text-sm">
              {invoice.houseType && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  invoice.houseType === 'dabaq' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                }`}>
                  Ka: {getHouseTypeLabel(invoice.houseType)}
                  {invoice.houseType === 'dabaq' && invoice.floorLevel
                    ? ` · ${getFloorLabel(invoice.floorLevel)}${invoice.dabaqNameFrom ? ` · ${invoice.dabaqNameFrom}` : ''}`
                    : ''}
                </span>
              )}
              {invoice.destinationType && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  invoice.destinationType === 'dabaq' ? 'bg-indigo-100 text-indigo-800' : 'bg-green-100 text-green-800'
                }`}>
                  U gurayso: {invoice.destinationType === 'dabaq' ? 'Dabaq / Biyaano' : getDestinationLabel(invoice.destinationType)}
                  {invoice.destinationType === 'dabaq' && invoice.destinationFloor
                    ? ` · ${getFloorLabel(invoice.destinationFloor)}${invoice.dabaqNameTo ? ` · ${invoice.dabaqNameTo}` : ''}`
                    : ''}
                </span>
              )}
              {getFeatureLabels(invoice.biyaanoFeatures).map((label, i) => (
                <span key={i} className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}

        {(invoice.workShifts?.length > 0 || invoice.shiftNotes) && (
          <div className="p-4 md:p-6 border-b bg-slate-50">
            <h3 className="text-xs font-medium text-gray-500 mb-2">Shift-ka shaqada</h3>
            <WorkShiftsEditor
              shifts={invoice.workShifts || []}
              employees={employees}
              readOnly
            />
            {invoice.shiftNotes && <p className="text-sm text-gray-600 mt-2">{invoice.shiftNotes}</p>}
          </div>
        )}

        {/* Items */}
        <div className="p-4 md:p-6 border-b">
          <h3 className="text-xs font-medium text-gray-500 mb-2">ALAABADA</h3>
          {renderItems()}
        </div>

        {/* Pricing */}
        <div className="p-4 md:p-6 border-b">
          <div className="max-w-xs ml-auto">
            <div className="flex justify-between py-1 text-sm">
              <span className="text-gray-600">Mashruuca:</span>
              <span>{formatCurrency(invoice.projectAmount)}</span>
            </div>
            <div className="flex justify-between py-1 text-sm">
              <span className="text-gray-600">Xamaali:</span>
              <span>{formatCurrency(invoice.xamaaliAmount)}</span>
            </div>
            <hr className="my-2" />
            <div className="flex justify-between py-1">
              <span className="font-bold">WADARTA:</span>
              <span className="font-bold text-red-600 text-lg">{formatCurrency(invoice.totalAmount)}</span>
            </div>
          </div>
        </div>

        {/* Payment */}
        <div className="p-4 md:p-6 border-b bg-blue-50">
          <div className="flex flex-col sm:flex-row justify-between gap-2">
            <div>
              <h3 className="text-xs font-medium text-gray-500 mb-1">LACAG BIXINTA</h3>
              <p className="font-bold">{invoice.paymentType}</p>
              {invoice.accountNumber && <p className="text-sm text-gray-600">Akoon: {invoice.accountNumber}</p>}
            </div>
            <div className="bg-blue-800 text-white px-3 py-1 rounded font-bold text-sm self-start">
              {invoice.paymentType}
            </div>
          </div>
        </div>

        {/* Signatures */}
        <div className="p-4 md:p-6 grid grid-cols-2 gap-4">
          <div className="text-center">
            <h3 className="text-xs font-medium text-gray-500 mb-2">MACMIILKA</h3>
            {invoice.customerSignature ? (
              <div className="border rounded p-2 bg-gray-50 inline-block">
                <img src={invoice.customerSignature} alt="Sig" className="max-h-12 md:max-h-16" />
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded p-4 text-gray-400 text-xs">
                N/A
              </div>
            )}
          </div>
          <div className="text-center">
            <h3 className="text-xs font-medium text-gray-500 mb-2">MAAMULKA</h3>
            {invoice.adminSignature ? (
              <div className="border rounded p-2 bg-gray-50 inline-block">
                <img src={invoice.adminSignature} alt="Sig" className="max-h-12 md:max-h-16" />
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded p-4 text-gray-400 text-xs">
                N/A
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-100 p-3 text-center">
          <img src="/logo.png" alt="Logo" className="h-5 mx-auto mb-1 opacity-50" />
          <p className="text-xs text-gray-500">Mahadsanid - SAHALGUUR SERVICES</p>
        </div>
      </div>

      {/* Revision History - no-print */}
      {invoice.revisionHistory && Array.isArray(invoice.revisionHistory) && invoice.revisionHistory.length > 0 && (
        <Card title="Taariikhda Bedelka" className="no-print">
          <div className="space-y-2">
            {invoice.revisionHistory.slice(-3).map((rev, idx) => (
              <div key={idx} className="flex items-start p-2 bg-gray-50 rounded text-sm">
                <span className="mr-2">✏️</span>
                <div>
                  <p className="font-medium">{rev.editedByName}</p>
                  <p className="text-xs text-gray-500">{rev.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Edit Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Wax ka bedel" size="sm">
        <div className="space-y-4">
          <Input
            label="Mashruuca ($)"
            name="projectAmount"
            type="number"
            value={editData.projectAmount}
            onChange={handleEditChange}
          />
          <Input
            label="Xamaali ($)"
            name="xamaaliAmount"
            type="number"
            value={editData.xamaaliAmount}
            onChange={handleEditChange}
          />
          <div className="bg-gray-50 rounded p-3">
            <div className="flex justify-between">
              <span className="font-bold">Wadarta:</span>
              <span className="font-bold text-red-600">
                {formatCurrency(editData.projectAmount + editData.xamaaliAmount)}
              </span>
            </div>
          </div>
          <Input
            label="Sababta"
            name="editReason"
            value={editData.editReason}
            onChange={handleEditChange}
            placeholder="Sababta..."
          />
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setShowEditModal(false)} fullWidth>
              Ka noqo
            </Button>
            <Button onClick={handleUpdate} loading={submitting} fullWidth>
              Kaydi
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Tirtir" size="sm">
        <div className="text-center">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🗑️</span>
          </div>
          <p className="text-gray-600 mb-4">Ma hubtaa inaad tirtirto <strong>{invoice.invoiceNumber}</strong>?</p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)} fullWidth>Maya</Button>
            <Button variant="danger" onClick={handleDelete} loading={submitting} fullWidth>Haa</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Add these helper functions inside the component
const getHouseTypeLabel = (type) => {
  const found = HOUSE_TYPES.find(h => h.value === type);
  return found ? found.label : type;
};

const getFloorLabel = (level) => {
  if (level === null || level === undefined || level === '') return '';
  const value = String(level);
  const found = FLOOR_OPTIONS.find(f => f.value === value);
  return found ? found.label : `Biyaanada ${value}aad`;
};

const getFeatureLabels = (features) => {
  if (!features || !Array.isArray(features)) return [];
  return features.map(f => {
    const found = BIYAANO_FEATURES.find(bf => bf.value === f);
    return found ? found.label : f;
  });
};

const getDestinationLabel = (type) => {
  const found = DESTINATION_TYPES.find(d => d.value === type);
  return found ? found.label : type;
};

export default InvoiceDetail;